import React from 'react';
import styled from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCreditCard, FaShieldAlt, FaChartLine, FaBolt, FaMobileAlt, FaFileInvoice } from 'react-icons/fa';
import theme from '../../theme';

const PaymentSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  margin-bottom: 5rem;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  &:nth-child(even) {
    direction: rtl;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      direction: ltr;
    }
  }
  
  &:nth-child(even) > div {
    direction: ltr;
  }
`;

const ContentText = styled.div`
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  ul {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
      line-height: 1.7;
    }
  }
`;

const ContentImage = styled.div`
  img {
    width: 100%;
    height: auto;
    border-radius: ${theme.borderRadius};
    box-shadow: ${theme.boxShadow};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: ${theme.colors.lightGray};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.boxShadow};
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: ${theme.colors.primary};
  margin-bottom: 1rem;
  text-align: center;
  svg { width: 40px; height: 40px; }
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes.medium};
  margin-bottom: 1rem;
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px;
  margin: 0 auto 2rem;
  font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const StepsSection = styled.div`
  margin: 4rem 0;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
`;

const Step = styled.div`
  display: flex;
  margin-bottom: 2.5rem;
  position: relative;
  
  &:last-child { margin-bottom: 0; }
  
  &:not(:last-child):before {
    content: '';
    position: absolute;
    top: 60px; left: 30px; bottom: -45px;
    width: 2px;
    background-color: ${theme.colors.primary};
    opacity: 0.3;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  width: 60px; height: 60px; min-width: 60px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; font-weight: 700;
  margin-right: 2rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) { margin-bottom: 1rem; }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: ${theme.fontSizes.large};
`;

const PaymentSolutionsPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const features = [
    { icon: <FaCreditCard />, key: 'multiPayment' },
    { icon: <FaShieldAlt />, key: 'secure' },
    { icon: <FaChartLine />, key: 'tracking' },
    { icon: <FaBolt />, key: 'integration' },
    { icon: <FaMobileAlt />, key: 'mobile' },
    { icon: <FaFileInvoice />, key: 'reporting' },
  ];

  const stepKeys = ['step1', 'step2', 'step3', 'step4'];
  
  return (
    <>
      <SEO titleKey="paymentSolutionsPage.seo.title" descriptionKey="paymentSolutionsPage.seo.description" />
      
      <PaymentSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('paymentPage.pageTitle')}</PageTitle>
            <PageSubtitle>{t('paymentPage.pageSubtitle')}</PageSubtitle>
          </PageHeader>
          
          <ContentSection>
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t('paymentPage.section1.title')}</SectionTitle>
                <p>{t('paymentPage.section1.paragraph1')}</p>
                <p>{t('paymentPage.section1.paragraph2')}</p>
                <ul>
                  <li>{t('paymentPage.section1.bullet1')}</li>
                  <li>{t('paymentPage.section1.bullet2')}</li>
                  <li>{t('paymentPage.section1.bullet3')}</li>
                  <li>{t('paymentPage.section1.bullet4')}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/payment-1.jpeg`} alt={t('paymentPage.section1.imageAlt')} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t('paymentPage.section2.title')}</SectionTitle>
                <p>{t('paymentPage.section2.paragraph1')}</p>
                <p>{t('paymentPage.section2.paragraph2')}</p>
                <ul>
                  <li>{t('paymentPage.section2.bullet1')}</li>
                  <li>{t('paymentPage.section2.bullet2')}</li>
                  <li>{t('paymentPage.section2.bullet3')}</li>
                  <li>{t('paymentPage.section2.bullet4')}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/payment-2.jpeg`} alt={t('paymentPage.section2.imageAlt')} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <StepsSection>
              <SectionTitle style={{ textAlign: 'center', marginBottom: '2rem' }}>
                {t('paymentPage.steps.title')}
              </SectionTitle>
              <StepsContainer>
                {stepKeys.map((key, index) => (
                  <Step key={key}>
                    <StepNumber>{index + 1}</StepNumber>
                    <StepContent>
                      <StepTitle>{t(`paymentPage.steps.${key}.title`)}</StepTitle>
                      <p>{t(`paymentPage.steps.${key}.description`)}</p>
                    </StepContent>
                  </Step>
                ))}
              </StepsContainer>
            </StepsSection>
            
            <SectionTitle style={{ textAlign: 'center', marginBottom: '2rem' }}>
              {t('paymentPage.features.title')}
            </SectionTitle>
            <FeatureGrid>
              {features.map((feature) => (
                <FeatureCard key={feature.key}>
                  <FeatureIcon>{feature.icon}</FeatureIcon>
                  <FeatureTitle>{t(`paymentPage.features.${feature.key}.title`)}</FeatureTitle>
                  <p>{t(`paymentPage.features.${feature.key}.description`)}</p>
                </FeatureCard>
              ))}
            </FeatureGrid>
          </ContentSection>
        </div>
      </PaymentSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t('paymentPage.cta.title')}</CTATitle>
          <CTAText>{t('paymentPage.cta.text')}</CTAText>
          <CTAButton to={`/${lang}/demo`}>{t('paymentPage.cta.button')}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default PaymentSolutionsPage;
